import { Component, ElementRef, AfterViewInit, ViewChild, OnDestroy } from '@angular/core'
import createScatterplot from 'regl-scatterplot'
import { fromEvent, Observable, Subject, take, takeUntil } from 'rxjs'
import * as d3 from 'd3'
import { Store } from '@ngrx/store'
import { selectionActions } from '../../../state/selection/selection.actions'
import {
    selectSelectionPoints,
    selectFreeSelectionColor,
    selectDisplayedOnScatterPlot,
} from '../../../state/selection/selection.selectors'
import { Actions, ofType } from '@ngrx/effects'
import {
    plotData$,
    PlotData,
    resetPlotData,
    plotFilterData$,
} from '../../../state/plot-data/plot-data'
import { plotDataActions } from '../../../state/plot-data/plot-data.actions'
import {
    selectIsLoadingPlotData,
    selectPointSize,
    selectPointOpacity,
    selectLassoType,
} from '../../../state/plot-data/plot-data.selectors'
import { userActions } from '../../../state/user/user.actions'
import { Color, ColorMap, ScatterplotMethodOptions } from 'regl-scatterplot/dist/types'
import { legendColor } from 'd3-svg-legend'
import { DatasetApiService } from 'src/app/shared/services/dataset-api.service'
@Component({
    selector: 'app-scatter-plot',
    templateUrl: './scatter-plot.component.html',
    styleUrls: ['./scatter-plot.component.scss'],
})
export class ScatterPlotComponent implements AfterViewInit, OnDestroy {
    @ViewChild('plotCanvas') plotCanvas: ElementRef<HTMLCanvasElement>
    @ViewChild('xAxis') xAxisContainer: ElementRef<SVGGElement>
    @ViewChild('yAxis') yAxisContainer: ElementRef<SVGGElement>
    @ViewChild('colorAxis') colorAxisContainer: ElementRef<SVGGElement>

    readonly isLoadingData$: Observable<boolean>
    readonly pointSize$: Observable<number>
    private pointSize: number
    readonly pointOpacity$: Observable<number>
    private pointOpacity: number
    readonly lassoType$: Observable<string>
    private lassoType: string

    private plot: ScatterPlot | null = null
    private segmentIds = new Uint32Array()
    private filterPoints: Set<number> | null = null

    private readonly xScale = d3.scaleLinear().domain([-1, 1])
    private readonly yScale = d3.scaleLinear().domain([-1, 1])
    private readonly zScale = d3
        .scaleSequential()
        .domain([0, 1])
        .interpolator(d3.interpolateViridis)

    private readonly xAxis = d3.axisBottom(this.xScale)
    private readonly yAxis = d3.axisRight(this.yScale)
    // https://d3-legend.susielu.com/#color-doc
    private readonly zLegend = legendColor()
        .cells(5)
        .shape('rect')
        .shapeWidth(10)
        .shapeHeight(10)
        .labelOffset(5)
        .labelFormat(d3.format(',.2r'))
        .shapePadding(5)
        .scale(this.zScale)
    private readonly defaultPointColorStrings = ['#575762', '#d5d5d5', '#a1a1a1']
    private pointColorStrings = this.defaultPointColorStrings.map(colorString =>
        d3.color(colorString)
    )
    private annotationColor: string
    private annotationColorActive: string
    private annotationColorUnsaved: string

    private readonly resizeObserver = new ResizeObserver(() => {
        this.onResize()
    })

    private lassoLongPressTime: number = 700
    private touchTimeout: number | null = null
    private lastPinchDistance: number | null = null
    private touchStartTime: number | null = null
    private touchMoveAllowed: boolean | null = null

    private readonly destroy$ = new Subject<void>()

    constructor(
        private readonly store: Store,
        private readonly actions$: Actions,
        private readonly datasetApiService: DatasetApiService
    ) {
        plotData$.pipe(takeUntil(this.destroy$)).subscribe(data => {
            this.drawPlot(data)
        })
        plotFilterData$.pipe(takeUntil(this.destroy$)).subscribe(filterPoints => {
            this.setFilterPoints(filterPoints)
        })

        this.store
            .select(selectSelectionPoints)
            .pipe(takeUntil(this.destroy$))
            .subscribe(points => {
                this.plot?.select(this.filteredSegmentIdsToPointIdxs(points), {
                    preventEvent: true,
                })
            })

        this.store
            .select(selectFreeSelectionColor)
            .pipe(takeUntil(this.destroy$))
            .subscribe(c => {
                this.annotationColorActive = c!
                this.plot?.set({
                    pointColorActive: this.pointColorStrings.map((color, i) => {
                        return i === 0 ? (c as Color) : color!.brighter().formatHex()
                    }) as any,
                })
            })

        this.store
            .select(selectDisplayedOnScatterPlot)
            .pipe(takeUntil(this.destroy$))
            .subscribe(selections => {
                if (selections.length === 0) {
                    this.pointColorStrings = this.defaultPointColorStrings.map(colorString =>
                        d3.color(colorString)
                    )
                    return
                }
                const updatedPointColorStrings = [
                    '#575762',
                    '#000000',
                    ...[...selections]!.map((selection: any) => selection.annotationColor),
                ].map(colorString => d3.color(colorString))
                this.pointColorStrings = updatedPointColorStrings
            })

        this.actions$
            .pipe(takeUntil(this.destroy$), ofType(userActions.startLogout))
            .subscribe(() => {
                resetPlotData()
                this.plot?.destroy()
            })

        this.isLoadingData$ = this.store
            .select(selectIsLoadingPlotData)
            .pipe(takeUntil(this.destroy$))

        this.pointSize$ = this.store.select(selectPointSize)
        this.pointOpacity$ = this.store.select(selectPointOpacity)
        this.lassoType$ = this.store.select(selectLassoType)
    }

    ngAfterViewInit(): void {
        this.resizeObserver.observe(this.plotCanvas.nativeElement)
        this.pointSize$.subscribe(size => {
            this.pointSize = size
            if (this.plot) {
                this.plot?.set({ pointSize: size })
            } else {
                this.plot = this.createPlotObject(this.plotCanvas)
            }
        })
        this.pointOpacity$.subscribe(opacity => {
            this.pointOpacity = opacity
            if (this.plot) {
                this.plot?.set({ opacity: opacity === 0 ? opacity : opacity / 10 })
            } else {
                this.plot = this.createPlotObject(this.plotCanvas)
            }
        })
        this.lassoType$.subscribe(type => {
            this.lassoType = type
            if (this.plot) {
                this.plot?.set({ lassoType: type } as any)
            } else {
                this.plot = this.createPlotObject(this.plotCanvas)
            }
        })
        this.onResize()
        fromEvent(window, 'resize')
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.onResize())
        fromEvent(window, 'orientationchange')
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.onResize())
    }

    ngOnDestroy(): void {
        this.destroy$.next()
        this.destroy$.complete()
        this.resizeObserver.disconnect()
    }

    resetCamera(): void {
        this.plot?.zoomToOrigin()
    }

    eventHandler(e: Event): void {
        if (!(e instanceof TouchEvent)) return

        if (e.type === 'touchstart') {
            this.touchStartTime = Date.now()
            this.touchMoveAllowed = true
        }

        if (e.type === 'touchmove') {
            if (e.touches.length !== 2) {
                const timeSinceStart = Date.now() - (this.touchStartTime || 0)
                if (timeSinceStart < this.lassoLongPressTime) {
                    this.touchMoveAllowed = false
                    e.preventDefault()
                    return
                }
                if (!this.touchMoveAllowed) {
                    e.preventDefault()
                    return
                }
            }
            e.preventDefault()
        }

        if (e.touches.length === 2) {
            if (this.touchTimeout) {
                clearTimeout(this.touchTimeout)
                this.touchTimeout = null
            }

            e.preventDefault()
            const touch1 = e.touches[0]
            const touch2 = e.touches[1]
            const distance = Math.hypot(
                touch2.clientX - touch1.clientX,
                touch2.clientY - touch1.clientY
            )

            if (
                this.lastPinchDistance !== null &&
                Math.abs(distance - this.lastPinchDistance) >= 10
            ) {
                const zoomDistance: number = 20
                const deltaY = distance > this.lastPinchDistance ? -zoomDistance : zoomDistance

                const wheelEvent = new WheelEvent('wheel', {
                    deltaX: 0,
                    deltaY: deltaY,
                    deltaZ: 0,
                    deltaMode: 0,
                    clientX: (touch1.clientX + touch2.clientX) / 2,
                    clientY: (touch1.clientY + touch2.clientY) / 2,
                    screenX: (touch1.screenX + touch2.screenX) / 2,
                    screenY: (touch1.screenY + touch2.screenY) / 2,
                    bubbles: true,
                    cancelable: true,
                    composed: true,
                    view: window,
                })

                e.target?.dispatchEvent(wheelEvent)

                this.lastPinchDistance = distance
            } else if (this.lastPinchDistance === null) {
                this.lastPinchDistance = distance
            }
            return
        }

        if (e.touches.length === 0) {
            this.lastPinchDistance = null
            this.touchStartTime = null
            this.touchMoveAllowed = false
        }

        if (e.touches.length === 1) {
            if (this.touchTimeout) return //
            this.touchTimeout = window.setTimeout(() => {
                const touch = e.touches[0] || e.changedTouches[0]
                const type =
                    e.type === 'touchstart'
                        ? 'mousedown'
                        : e.type === 'touchend'
                        ? 'mouseup'
                        : 'mousemove'
                const mouseEvent = new MouseEvent(type, {
                    clientX: touch.clientX,
                    clientY: touch.clientY,
                    screenX: touch.screenX,
                    screenY: touch.screenY,
                    button: 0,
                    buttons: 1,
                    relatedTarget: null,
                    bubbles: true,
                    cancelable: true,
                    composed: true,
                    detail: 1,
                    view: e.view,
                })
                e.target?.dispatchEvent(mouseEvent)

                this.touchTimeout = null
            }, 30)
        }

        if (e.type === 'touchend' && e.changedTouches.length === 1) {
            // e.preventDefault()
            const touch = e.changedTouches[0]
            const mouseEvent = new MouseEvent('mouseup', {
                clientX: touch.clientX,
                clientY: touch.clientY,
                screenX: touch.screenX,
                screenY: touch.screenY,
                button: 0,
                buttons: 1,
                relatedTarget: e.target,
                bubbles: true,
                cancelable: true,
                composed: true,
            })
            e.target?.dispatchEvent(mouseEvent)
        }
    }

    private onResize(): void {
        const clientRect = this.plotCanvas.nativeElement.getBoundingClientRect()
        // wrapping the resizing within a timeout fixes a timing issue (not sure why)
        setTimeout(() => {
            d3.select(this.xAxisContainer.nativeElement)
                .attr('transform', `translate(0, ${clientRect.height})`)
                .call(this.xAxis)
            d3.select(this.yAxisContainer.nativeElement)
                .attr('transform', `translate(${clientRect.width}, 0)`)
                .call(this.yAxis)
            d3.select(this.colorAxisContainer.nativeElement).attr('transform', `translate(20, 50)`)
            d3.select(this.colorAxisContainer.nativeElement)
                .selectAll('.label')
                .style('font-size', '0.5rem')
                .style('fill', 'var(--app-color-neutral-60)')
        })
    }

    private getSegmentColors(colorSeed: number, segmentIds: number[]): Promise<string[]> {
        return new Promise(resolve => {
            this.datasetApiService
                .getSegmentColors(colorSeed, segmentIds)
                .pipe(take(1))
                .subscribe(colors => {
                    resolve(colors)
                })
        })
    }

    private async drawPlot(data: PlotData): Promise<void> {
        if (!this.plot) return

        if (!data.domainX || !data.domainY) {
            this.plot.clear()
            this.plot.reset()
            this.segmentIds = new Uint32Array()
            return
        }
        data.points = uniquePointId(data.points.reverse())
        let pointColorStrings = ['#575762', '#d5d5d5', '#a1a1a1'].map(colorString =>
            d3.color(colorString)
        )

        let pointColor: ColorMap = this.pointColorStrings.map((color, i) => {
            return i === 0 && this.annotationColor
                ? (this.annotationColor as Color)
                : color!.formatHex()
        })

        let pointColorActive: ColorMap = this.pointColorStrings.map((color, i) => {
            return i === 0
                ? this.annotationColorUnsaved || (this.annotationColorActive as Color)
                : color!.brighter().formatHex()
        }) as any
        let pointColorHover: ColorMap = this.pointColorStrings.map(color =>
            color!.brighter().formatHex()
        )

        let options: ScatterplotMethodOptions['draw'] = {
            zDataType: 'categorical',
        }
        let colorPointConversion = (color: number): number => {
            return color
        }

        if (data.domainColor) {
            const sortedValues = [...data.points]
                .map(
                    p =>
                        ((p[3] + 1) * data.domainColor?.upperBound! -
                            data.domainColor?.lowerBound!) /
                            2 +
                        data.domainColor?.lowerBound!
                )
                .sort((a, b) => a - b)
            const lowerPercentileIndex = Math.floor(0.01 * sortedValues.length)
            const upperPercentileIndex = Math.min(
                Math.ceil(0.99 * sortedValues.length),
                sortedValues.length - 1
            )
            const lowerBound = sortedValues[lowerPercentileIndex]
            const upperBound = sortedValues[upperPercentileIndex]
            this.zScale.domain([lowerBound, upperBound])
            const colorScale = this.zScale
                .copy()
                .domain([calcColorScale(lowerBound), calcColorScale(upperBound)])
            const viridisColors = Array.from({ length: 100 }, (_, i) => d3.color(colorScale(i))!)

            pointColor = viridisColors.map(color => color.formatHex())
            pointColorActive = viridisColors.map(color => color.brighter().formatHex())
            pointColorHover = viridisColors.map(color => color.darker().formatHex())

            options = { zDataType: 'continuous' }
            // color axis has to be normalized to 0..1 instead of -1..1
            colorPointConversion = (color: number) => {
                return (color + 1) / 2
            }
            if (data.legend) {
                data.points = [...data.points].map(p => [
                    p[0],
                    p[1],
                    p[2],
                    conditionalRound(
                        data.legend,
                        ((p[3] + 1) * data.domainColor?.upperBound! -
                            data.domainColor?.lowerBound!) /
                            2 +
                            data.domainColor?.lowerBound!
                    ),
                ])

                options = { zDataType: 'categorical' }
                const zlegendDomain = data.legend.map(l => l.value)
                const categoricalColor = await this.getSegmentColors(
                    1,
                    data.legend.map(l => l.key)
                )

                const zlegendColor = data.legend.map(
                    (_, i) => categoricalColor[i % categoricalColor.length]
                )
                const ordinal = d3.scaleOrdinal().domain(zlegendDomain).range(zlegendColor)
                d3.select(this.colorAxisContainer.nativeElement)
                    .attr('visibility', 'visible')
                    .call(
                        legendColor()
                            .shape('rect')
                            .shapeWidth(10)
                            .shapeHeight(10)
                            .labelOffset(5)
                            .labelFormat(d3.format(',.2r'))
                            .shapePadding(5)
                            .scale(ordinal) as any
                    )
                this.colorAxisContainer.nativeElement
                pointColorStrings = ['#ffffff', ...zlegendColor].map(
                    (
                        colorString //points with value 0 will be white
                    ) => d3.color(colorString)
                )
                pointColor = pointColorStrings.map(color => color!.formatHex())
                pointColorActive = pointColorStrings.map(color => color!.brighter().formatHex())
                pointColorHover = pointColorStrings.map(color => color!.brighter().formatHex())
            } else {
                d3.select(this.colorAxisContainer.nativeElement)
                    .attr('visibility', 'visible')
                    .call(this.zLegend as any)
            }
            this.colorAxisContainer.nativeElement
            function calcColorScale(bound: number) {
                return (
                    ((bound - data.domainColor?.lowerBound!) /
                        (data.domainColor?.upperBound! - data.domainColor?.lowerBound!)) *
                    100
                )
            }
        } else {
            d3.select(this.colorAxisContainer.nativeElement).attr('visibility', 'hidden')
        }

        this.plot.set({
            colorBy: 'valueA',
            xScale: d3.scaleLinear().domain([data.domainX.lowerBound, data.domainX.upperBound]),
            yScale: d3.scaleLinear().domain([data.domainY.lowerBound, data.domainY.upperBound]),
            pointColor: pointColor,
            // library types are wrong
            pointColorActive: pointColorActive as any,
            pointColorHover: pointColorHover as any,
            // also start lasso mode by clicking on an empty part (alternative to holding shift)
            lassoInitiator: true,
            lassoOnLongPress: true,
            lassoLongPressTime: this.lassoLongPressTime,
        })
        this.plot.clear()
        this.plot.reset()

        // TODO(ARIADNE-336): For better touch support we could start in lasso mode, and/or add buttons:
        // this.plot.set({ mouseMode: 'lasso' })
        // this.plot.set({ mouseMode: 'panZoom' })

        this.segmentIds = new Uint32Array(data.points.map(point => point[0]))
        this.plot
            .draw(
                data.points.map(point => [point[1], point[2], colorPointConversion(point[3])]),
                options
            )
            .then(() => {
                this.store.dispatch(plotDataActions.plotDrawn())
            })

        function uniquePointId(arrays: [number, number, number, number][]) {
            const seen = new Set()
            const uniquePoints = []

            for (const arr of arrays) {
                if (!seen.has(arr[0])) {
                    uniquePoints.push(arr)
                    seen.add(arr[0])
                }
            }

            return uniquePoints
        }

        function conditionalRound(legend: any, num: number) {
            const rounded = Math.round(num)
            return (
                (legend.findIndex((l: { key: number; value: number }) => l.key === rounded) + 1) *
                    2 || 0
            )
        }
    }

    private setFilterPoints(filterPoints: Set<number> | null): void {
        this.filterPoints = filterPoints
    }

    private pointIdxsToSegmentIds(pointIdxs: number[]): number[] {
        return Array.from(pointIdxs.map(idx => this.segmentIds[idx]))
    }

    private filteredSegmentIdsToPointIdxs(segmentIds: Set<number>): number[] {
        let segmentIdsArray = [...segmentIds]
        if (this.filterPoints) {
            segmentIdsArray = segmentIdsArray.filter(point => this.filterPoints?.has(point))
        }
        return this.segmentIdsToPointIdxs(new Set(segmentIdsArray))
    }

    private segmentIdsToPointIdxs(segmentIds: Set<number>): number[] {
        const result = []
        for (let i = 0; i < this.segmentIds.length; i++) {
            if (segmentIds.has(this.segmentIds[i])) {
                result.push(i)
            }
        }
        return result
    }

    private createPlotObject(container: ElementRef): ScatterPlot {
        // todo: this function breaks if the plot panel is not visible on load
        const plot = createScatterplot({
            canvas: container.nativeElement,
            pointSize: this.pointSize,
            opacity: this.pointOpacity,
            lassoColor: '#e3443e',
            lassoType: this.lassoType,
            xScale: this.xScale,
            yScale: this.yScale,
        } as any)

        plot.subscribe(
            'select',
            // @ts-ignore: Typings for eventHandler from lib are incorrect
            (selection: { points: number[] }) => {
                let points = this.pointIdxsToSegmentIds(selection.points)
                if (this.filterPoints) {
                    points = points.filter(point => this.filterPoints!.has(point))
                }
                this.store.dispatch(
                    selectionActions.updateSelection({
                        points: points,
                    })
                )
            },
            0
        )

        plot.subscribe(
            'view',
            ({ xScale, yScale }) => {
                if (xScale != null) {
                    d3.select(this.xAxisContainer.nativeElement).call(this.xAxis.scale(xScale))
                }
                if (yScale != null) {
                    d3.select(this.yAxisContainer.nativeElement).call(this.yAxis.scale(yScale))
                }
                // Maintain legend position and visibility during view changes
                d3.select(this.colorAxisContainer.nativeElement).attr(
                    'transform',
                    `translate(20, 50)`
                )
                d3.select(this.colorAxisContainer.nativeElement)
                    .selectAll('.label')
                    .style('font-size', '0.5rem')
                    .style('fill', 'var(--app-color-neutral-60)')
            },
            0
        )

        return plot
    }

    preventDefault(e: MouseEvent): void {
        // This prevents dragging the lasso or panning beyond the canvas border
        //  from selecting text and other elements under the cursor
        e.preventDefault()
    }
}

type ScatterPlot = ReturnType<typeof createScatterplot>
